/**
 * 费用计算显示组件
 * 实时显示任务消耗的算力
 */

import type { ReactElement } from 'react'
import { useMemo } from 'react'
import { motion } from 'framer-motion'
import { Zap, Image as ImageIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { FormComponent, FormState } from '@/types/workflow'

/**
 * 倍数明细项
 */
interface MultiplierItem {
  /** 参数标题 */
  title: string
  /** 选中的选项标签 */
  optionLabel: string
  /** 倍数值 */
  multiplier: number
}

/**
 * 从表单组件和状态中提取倍数明细
 * @param components 表单组件配置
 * @param formState 表单状态
 * @returns 倍数明细列表
 */
function extractMultipliers(
  components: FormComponent[] | undefined,
  formState: FormState | undefined
): MultiplierItem[] {
  if (components === undefined || formState === undefined) {
    return []
  }

  const items: MultiplierItem[] = []

  components.forEach((component) => {
    if (component.options && component.options.length > 0) {
      const selectedValue = formState[component.param_name]
      const selectedOption = component.options.find(
        (opt) => opt.value === selectedValue
      )
      // 只显示有 billing_weight 且不为 1 的选项
      if (
        selectedOption?.billing_weight !== undefined &&
        selectedOption.billing_weight !== 1
      ) {
        items.push({
          title: component.title,
          optionLabel: selectedOption.label,
          multiplier: selectedOption.billing_weight,
        })
      }
    }
  })

  return items
}

/**
 * 费用计算组件属性
 */
interface CostCalculatorProps {
  /** 基础算力消耗 */
  basePower: number
  /** 计算后的总算力 */
  totalCost: number
  /** 图片数量 */
  imageCount: number
  /** 提示词数量 */
  promptCount?: number
  /** 用户当前算力 */
  userPower?: number
  /** 紧凑模式（水平布局） */
  compact?: boolean
  /** 嵌入模式（无边框和背景，用于嵌入到其他卡片中） */
  embedded?: boolean
  /** 表单组件配置（用于显示倍数明细） */
  formComponents?: FormComponent[]
  /** 表单状态（用于显示倍数明细） */
  formState?: FormState
  /** 自定义类名 */
  className?: string
  /** 是否启用多组提示词 */
  enableMultiPrompt?: boolean
  /** 重复提交次数 */
  repeatCount?: number
  /** 原生多图输出倍率 */
  nativeOutputMultiplier?: number
  /** 预计输出图片数量 */
  expectedOutputCount?: number
}

/**
 * 费用计算显示组件
 * @param props 组件属性
 * @returns 组件元素
 */
export function CostCalculator({
  basePower,
  totalCost,
  imageCount,
  promptCount,
  userPower,
  compact = false,
  embedded = false,
  formComponents,
  formState,
  className,
  enableMultiPrompt = false,
  repeatCount = 1,
  nativeOutputMultiplier = 1,
  expectedOutputCount,
}: CostCalculatorProps): ReactElement {
  const effectiveRepeatCount = repeatCount > 1 ? repeatCount : 1
  const effectiveNativeOutputMultiplier = nativeOutputMultiplier > 1 ? nativeOutputMultiplier : 1
  const displayedTotalCost = totalCost * effectiveRepeatCount
  const isInsufficient = userPower !== undefined && userPower < displayedTotalCost

  // 提取倍数明细
  const multipliers = useMemo(
    () => extractMultipliers(formComponents, formState),
    [formComponents, formState]
  )

  // 紧凑模式：水平布局
  if (compact) {
    return (
      <div className={cn('flex items-center gap-4', className)}>
        {/* 图片数量 */}
        {imageCount > 0 && (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <ImageIcon className="h-4 w-4" />
            <span>{imageCount} 张</span>
          </div>
        )}

        {/* 预计消耗 */}
        <div className="flex items-center gap-1.5">
          <Zap className="h-4 w-4 text-primary" />
          <span className="text-sm text-muted-foreground">预计消耗</span>
          <motion.span
            key={displayedTotalCost}
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            className="text-base font-bold"
          >
            {displayedTotalCost}
          </motion.span>
          <span className="text-sm text-muted-foreground">算力</span>
        </div>

        {/* 算力不足提示 */}
        {isInsufficient && (
          <span className="text-xs text-destructive">
            （余额不足）
          </span>
        )}
      </div>
    )
  }

  // 默认模式：卡片布局（embedded 模式不显示外层样式）
  return (
    <div
      className={cn(
        !embedded && 'rounded-xl border bg-card p-4 shadow-sm',
        className
      )}
    >
      <div className="space-y-3">
        {/* 标题 */}
        <div className="flex items-center gap-2 text-sm font-medium">
          <Zap className="h-4 w-4 text-primary" />
          <span>费用预估</span>
        </div>

        {/* 详细计算 */}
        <div className="space-y-2 text-sm">
          {/* 基础消耗 */}
          <div className="flex items-center justify-between text-muted-foreground">
            <span>单张消耗</span>
            <span>{basePower} 算力</span>
          </div>

          {/* 图片数量 */}
          {imageCount > 0 && (
            <div className="flex items-center justify-between text-muted-foreground">
              <span className="flex items-center gap-1">
                <ImageIcon className="h-3.5 w-3.5" />
                图片数量
              </span>
              <span>x {imageCount}</span>
            </div>
          )}

          {/* 提示词数量 - 仅在启用多组提示词时显示 */}
          {enableMultiPrompt && promptCount !== undefined && promptCount > 1 && (
            <div className="flex items-center justify-between text-muted-foreground">
              <span>提示词数量</span>
              <span>x {promptCount}</span>
            </div>
          )}

          {/* 总生成数量（M x N） - 仅在启用多组提示词时显示 */}
          {enableMultiPrompt && imageCount > 1 && promptCount !== undefined && promptCount > 1 && (
            <div className="flex items-center justify-between text-muted-foreground">
              <span>总生成数量</span>
              <span>{imageCount} x {promptCount} = {imageCount * promptCount} 张</span>
            </div>
          )}

          {/* 倍数明细 */}
          {multipliers.map((item, index) => (
            <div
              key={index}
              className="flex items-center justify-between text-muted-foreground"
            >
              <span className="truncate max-w-[120px]" title={item.title}>
                {item.title}
              </span>
              <span className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground/70">
                  {item.optionLabel}
                </span>
                <span className="font-medium text-primary">
                  x{item.multiplier}
                </span>
              </span>
            </div>
          ))}

          {effectiveRepeatCount > 1 && (
            <div className="flex items-center justify-between text-muted-foreground">
              <span>重复生成</span>
              <span>x {effectiveRepeatCount}</span>
            </div>
          )}

          {effectiveNativeOutputMultiplier > 1 && (
            <div className="flex items-center justify-between text-muted-foreground">
              <span>原生多图</span>
              <span>x {effectiveNativeOutputMultiplier}</span>
            </div>
          )}

          {expectedOutputCount !== undefined && expectedOutputCount > 1 && (
            <div className="flex items-center justify-between text-muted-foreground">
              <span>预计结果数</span>
              <span>{expectedOutputCount} 张</span>
            </div>
          )}

          {/* 分隔线 */}
          <div className="border-t" />

          {/* 总计 */}
          <div className="flex items-center justify-between">
            <span className="font-medium">预计消耗</span>
            <motion.span
              key={displayedTotalCost}
              initial={{ scale: 1.1, color: 'hsl(var(--primary))' }}
              animate={{ scale: 1, color: 'inherit' }}
              className="text-lg font-bold"
            >
              {displayedTotalCost} 算力
            </motion.span>
          </div>
        </div>

        {/* 余额提示 */}
        {userPower !== undefined && (
          <div
            className={cn(
              'flex items-center justify-between rounded-lg px-3 py-2 text-sm',
              isInsufficient
                ? 'bg-destructive/10 text-destructive'
                : 'bg-muted text-muted-foreground'
            )}
          >
            <span>当前算力</span>
            <span className="font-medium">{userPower}</span>
          </div>
        )}

        {/* 算力不足警告 */}
        {isInsufficient && (
          <p className="text-xs text-destructive">
            算力不足，还需 {displayedTotalCost - userPower} 算力
          </p>
        )}
      </div>
    </div>
  )
}
